import { useState } from "react";
import { Building2, ChevronDown, CheckCircle2, MessageSquareReply, Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ComplaintForm } from "@/components/ComplaintForm";

const STATUS = {
  aberta: { label: "Aguardando resposta", cls: "bg-red-50 text-[#dc2626] border-red-200" },
  respondida: { label: "Respondida", cls: "bg-amber-50 text-[#d97706] border-amber-200" },
  resolvida: { label: "Resolvida", cls: "bg-[#e8f0ec] text-[#1b5e3f] border-[#1b5e3f]/20" },
};

export function ComplaintCard({ complaint, user, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const st = STATUS[complaint.status] || STATUS.aberta;
  const isOwner = user && (user.id === complaint.user_id || user.role === "admin");

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/complaints/${complaint.id}/resolve`);
      toast.success("Reclamação marcada como resolvida!");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Tem certeza que deseja remover esta reclamação? Esta ação não pode ser desfeita.")) return;
    setBusy(true);
    try {
      await api.delete(`/complaints/${complaint.id}`);
      toast.success("Reclamação removida.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`complaint-card-${complaint.id}`} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left p-5 hover:bg-[#fafaf8] transition-colors" data-testid={`complaint-toggle-${complaint.id}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl bg-[#e8f0ec] flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-[#1b5e3f]" />
            </span>
            <div className="min-w-0">
              <p className="font-head font-semibold text-[#1c1917] truncate">{complaint.company_name}</p>
              <p className="text-sm text-[#57534e] truncate">{complaint.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
            <ChevronDown className={`w-4 h-4 text-[#78716c] transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </div>
        <p className="text-xs text-[#a8a29e] mt-2">por {complaint.user_name} · {new Date(complaint.created_at).toLocaleDateString("pt-PT")}</p>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#e7e5e4] pt-4">
          <p className="text-sm text-[#1c1917] whitespace-pre-wrap leading-relaxed">{complaint.description}</p>

          {complaint.images?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {complaint.images.map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt="Prova anexada" className="w-24 h-24 rounded-xl object-cover border border-[#e7e5e4] hover:opacity-80 transition-opacity" />
                </a>
              ))}
            </div>
          )}

          {complaint.response && (
            <div className="mt-4 bg-[#e8f0ec]/60 border border-[#1b5e3f]/15 rounded-xl p-4">
              <p className="text-xs font-bold text-[#1b5e3f] uppercase tracking-wide flex items-center gap-1.5">
                <MessageSquareReply className="w-3.5 h-3.5" /> Resposta de {complaint.company_name}
              </p>
              <p className="text-sm text-[#1c1917] mt-2 whitespace-pre-wrap">{complaint.response}</p>
              {complaint.responded_at && <p className="text-xs text-[#a8a29e] mt-2">{new Date(complaint.responded_at).toLocaleDateString("pt-PT")}</p>}
            </div>
          )}

          {isOwner && (
            <div className="flex flex-wrap gap-2 mt-4">
              {complaint.status !== "resolvida" && (
                <Button data-testid={`complaint-resolve-btn-${complaint.id}`} onClick={resolve} disabled={busy} variant="outline" className="rounded-xl border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec]">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Marcar como resolvida</>}
                </Button>
              )}
              <Button data-testid={`complaint-edit-btn-${complaint.id}`} onClick={() => setEditOpen(true)} disabled={busy} variant="outline" className="rounded-xl border-[#e7e5e4] text-[#57534e] hover:bg-[#fafaf8]">
                <Pencil className="w-4 h-4 mr-2" /> Editar
              </Button>
              <Button data-testid={`complaint-delete-btn-${complaint.id}`} onClick={remove} disabled={busy} variant="outline" className="rounded-xl border-red-200 text-[#dc2626] hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Remover
              </Button>
            </div>
          )}
        </div>
      )}

      <ComplaintForm open={editOpen} onClose={() => setEditOpen(false)} onCreated={onChanged} complaint={complaint} />
    </div>
  );
}
